import { useState, useEffect, useContext, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";

import { Card } from "@mui/material";

import toast from "react-hot-toast";
import moment from "moment";

import Prompt from "components/UpStyled/Prompt";
import api from "services/api";
import SuiTypography from "components/SuiTypography";
import SuiBadgeDot from "components/SuiBadgeDot";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";

import { AccountContext } from "context/account";

import DoneIcon from "@mui/icons-material/Done";
import UndoIcon from "@mui/icons-material/Undo";
import AddIcon from "@mui/icons-material/Add";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import AddEventModal from "pages/de/dashboard/components/AddEventModal";
import EventModalViewer from "pages/de/dashboard/components/EventModalViewer";

import { getFileImage, getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import { matchResourceUrl, convertDbToEvent } from "utils/helper";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";

const EventList = ({
  tab,
  data,
  ContainerHeight = 60,
  appendData = () => {},
  handleRefresh = () => {},
}) => {
  const [addEventVisible, setAddEventVisible] = useState(false);
  const [editEventVisible, setEditEventVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState({});
  const [isShowCompleted, setIsShowCompleted] = useState(false);
  const [viewEventVisible, setViewEventVisible] = useState(false);

  const newSelectedInfo = {
    date: moment().startOf("minute").toDate(),
    allDay: false,
  };

  const account = useContext(AccountContext);

  const handleMarkStatus = async (id, status) => {
    try {
      let payload = {
        status: status,
      };

      const newCreatedEvent = await api.calendar.updateEvent(id, payload);
      toast.success(`Mark ${status} successfully!`);

      handleRefresh();
    } catch (err) {
      console.log(err);
      toast.error(`Mark ${status} failed!`);
    }
  };

  return (
    <Card sx={{ px: 2, py: 2, height: 550 }}>
      <SuiBox
        sx={{ mb: 1, mx: 1 }}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <SuiButton
          variant="text"
          color="dark"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={() => setAddEventVisible(true)}
        >
          <AddIcon fontSize="small" />
          Create new task
        </SuiButton>
        <SuiBox>
          <SuiTypography variant="body2" fontWeight="bold" display="inline" sx={{ mr: 0.5 }}>
            Show Completed
          </SuiTypography>
          <Switch
            checked={isShowCompleted}
            onChange={(e) => {
              const isCheckd = e?.target?.checked;
              setIsShowCompleted(isCheckd);
            }}
          />
        </SuiBox>
      </SuiBox>

      <List sx={{ px: 1, overflow: "auto" }}>
        {data.map((e, index) => {
          const date = moment(e?.start_time).format("MMM DD,YYYY");
          const title = e?.title;
          if (!isShowCompleted && e.status == "completed") {
            return null;
          }

          return (
            <ListItem
              disablePadding
              sx={{
                borderBottom: index == data.length ? "none" : `${colors.light.main} solid 1px`,
              }}
            >
              <SuiBox display="flex" justifyContent="space-between" sx={{ width: "100%", my: 1 }}>
                <SuiBox>
                  <SuiBadgeDot
                    color={e.status == "completed" ? "success" : "info"}
                    style={{ display: "inline-block" }}
                  />
                  <SuiTypography
                    sx={{
                      display: "inline",
                    }}
                    className={`${e.status == "completed" ? "line-through" : ""}`}
                    color={e.status == "completed" ? "secondary" : "dark"}
                  >
                    <strong>{date}</strong>{" "}
                    <span
                      style={{ marginLeft: 8, cursor: "pointer" }}
                      onClick={() => {
                        setSelectedEvent(e);
                        setViewEventVisible(true);
                      }}
                    >
                      {title}
                    </span>
                  </SuiTypography>
                  {e?.content?.resources ? (
                    <SuiBox ml={3} display="flex" gap={0.5}>
                      {" "}
                      {e.content.resources.map((item) => {
                        let img = {};
                        let title = "";
                        if (item.resourceType == "style") {
                          const styleImages = getStyleImages(item, false);
                          img = styleImages[0];
                          title = item?.title;
                        } else if (item.resourceType == "moodboard") {
                          img = item.img;
                          title = item?.name;
                        } else if (item.resourceType == "project") {
                          const products = item?.products || [];
                          let styleImages = [];
                          //add style first image
                          for (let i = 0; i < products.length; i++) {
                            const temp = getStyleImages(products[i], false);
                            styleImages.push(...temp);
                          }
                          img = styleImages[0];
                          title = item?.name;
                        } else if (item.resourceType == "collection") {
                          img = item.moodboard?.[0]?.img;
                          title = item?.title;
                        }

                        return (
                          <SuiBox sx={{ width: 40, height: 40 }}>
                            <Tooltip title={title}>
                              <Link to={matchResourceUrl(item)}>
                                <ImageCardWrapper
                                  data={img}
                                  mode={"normal"}
                                  height={40}
                                  width={40}
                                  iconMode
                                />
                              </Link>
                            </Tooltip>
                          </SuiBox>
                        );
                      })}
                    </SuiBox>
                  ) : null}
                </SuiBox>
                <SuiBox>
                  {e.status == "completed" ? (
                    <Tooltip title="Mark uncompleted">
                      <UndoIcon
                        color="error"
                        fontSize="small"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleMarkStatus(e.id, "uncompleted")}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Mark completed">
                      <DoneIcon
                        color="success"
                        fontSize="small"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleMarkStatus(e.id, "completed")}
                      />
                    </Tooltip>
                  )}
                </SuiBox>
              </SuiBox>
            </ListItem>
          );
        })}
      </List>
      {addEventVisible && selectedEvent ? (
        <AddEventModal
          visible={addEventVisible}
          selectInfo={newSelectedInfo}
          handleClose={() => setAddEventVisible(false)}
          handleRefresh={handleRefresh}
          selectedType={"task"}
        />
      ) : null}
      {editEventVisible && selectedEvent ? (
        <AddEventModal
          visible={editEventVisible}
          selectInfo={{ event: convertDbToEvent(selectedEvent) }}
          isEdit={true}
          handleClose={() => setEditEventVisible(false)}
          handleRefresh={handleRefresh}
          selectedType={"task"}
        />
      ) : null}
      {viewEventVisible && selectedEvent ? (
        <EventModalViewer
          visible={viewEventVisible}
          selectInfo={{ event: convertDbToEvent(selectedEvent) }}
          handleOpenEdit={() => {
            setViewEventVisible(false);
            setEditEventVisible(true);
          }}
          handleClose={() => setViewEventVisible(false)}
          handleRefresh={handleRefresh}
        />
      ) : null}
    </Card>
  );
};

export default EventList;
